import * as React from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { CheckCircle2, Download, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import { DataTable } from "@/components/data-table";
import { errorText, PageHeader, Spinner, StatusBadge } from "@/components/common";
import { api, download } from "@/lib/api";
import { dateTime, money, shortId, type Money } from "@/lib/format";
import { label } from "@/lib/labels";
import { useAuth } from "@/lib/auth";

interface SummaryLine {
  account: string;
  entryType: string;
  amount: Money;
  tax: Money;
  entries: number;
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
function monthRange(): [string, string] {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return [isoDay(from), isoDay(to)];
}

export function AccountingPage() {
  const { can } = useAuth();
  const [[from, to], setRange] = React.useState(monthRange);
  const [busy, setBusy] = React.useState(false);
  const valid = from < to;
  const q = useQuery({
    queryKey: ["admin-ledger", from, to],
    queryFn: () =>
      api<{ lines: SummaryLine[]; balanced: boolean }>(
        `/v1/admin/accounting/summary?from=${from}&to=${to}`,
      ),
    enabled: valid,
  });
  const columns: ColumnDef<SummaryLine, unknown>[] = [
    { accessorKey: "account", header: "Hesap", cell: ({ row }) => label(row.original.account) },
    {
      accessorKey: "entryType",
      header: "Kayıt türü",
      cell: ({ row }) => label(row.original.entryType),
    },
    {
      id: "amount",
      header: "Tutar",
      cell: ({ row }) => <span className="tabular">{money(row.original.amount)}</span>,
    },
    {
      id: "tax",
      header: "KDV",
      cell: ({ row }) => (
        <span className="tabular text-muted-foreground">{money(row.original.tax)}</span>
      ),
    },
    { accessorKey: "entries", header: "Satır" },
  ];
  async function exportCsv() {
    setBusy(true);
    try {
      await download(`/v1/admin/accounting/export?from=${from}&to=${to}`);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHeader
        title="Muhasebe"
        description="Çift taraflı kayıtların dönem özeti (K-089). Her para biriminde toplam 0 olmalı."
        actions={
          can("accounting.exports.run") && (
            <Button variant="outline" onClick={() => void exportCsv()} disabled={!valid || busy}>
              {busy ? <Spinner /> : <Download />} CSV indir
            </Button>
          )
        }
      />
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-4 p-4">
          <div className="grid gap-1.5">
            <Label htmlFor="from">Başlangıç</Label>
            <Input
              id="from"
              type="date"
              value={from}
              onChange={(e) => setRange([e.target.value, to])}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="to">Bitiş (hariç)</Label>
            <Input
              id="to"
              type="date"
              value={to}
              onChange={(e) => setRange([from, e.target.value])}
            />
          </div>
          {q.data &&
            (q.data.balanced ? (
              <span className="flex items-center gap-1.5 text-sm text-success">
                <CheckCircle2 className="size-4" /> Kayıtlar dengeli
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-sm text-destructive">
                <TriangleAlert className="size-4" /> Denge bozuk: mutabakat gerekli
              </span>
            ))}
          {!valid && (
            <span className="text-sm text-destructive">Bitiş, başlangıçtan sonra olmalı.</span>
          )}
        </CardContent>
      </Card>
      <DataTable
        columns={columns}
        data={q.data?.lines}
        isLoading={valid && q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Bu dönemde kayıt yok."
      />
    </>
  );
}

interface PayoutRow {
  id: string;
  status: string;
  amount: Money;
  taskId: string | null;
  scheduledFor: string | null;
  paidAt: string | null;
  failureReason: string | null;
  provider: { id: string; displayName: string; payoutAccount: string | null };
}

const VIEWS = {
  stuck: "Takılan",
  pending: "Bekleyen",
  debt: "İade borcu",
  paid: "Ödenen",
} as const;

export function PayoutsPage() {
  const [view, setView] = React.useState<keyof typeof VIEWS>("stuck");
  const q = useQuery({
    queryKey: ["admin-payouts", view],
    queryFn: () => api<{ payouts: PayoutRow[] }>(`/v1/admin/payouts?view=${view}&limit=100`),
  });
  const columns: ColumnDef<PayoutRow, unknown>[] = [
    {
      id: "provider",
      header: "Hizmet veren",
      cell: ({ row }) => (
        <Link
          className="text-primary hover:underline"
          to={`/providers/${row.original.provider.id}`}
        >
          {row.original.provider.displayName}
        </Link>
      ),
    },
    {
      id: "amount",
      header: "Tutar",
      cell: ({ row }) => <span className="tabular font-medium">{money(row.original.amount)}</span>,
    },
    {
      accessorKey: "status",
      header: "Durum",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "task",
      header: "İş",
      cell: ({ row }) =>
        row.original.taskId ? (
          <Link className="hover:underline" to={`/tasks/${row.original.taskId}`}>
            {shortId(row.original.taskId)}
          </Link>
        ) : (
          "—"
        ),
    },
    {
      id: "when",
      header: view === "paid" ? "Ödendi" : "Plan",
      cell: ({ row }) =>
        dateTime(view === "paid" ? row.original.paidAt : row.original.scheduledFor),
    },
    {
      id: "why",
      header: "Not",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.failureReason ??
            (row.original.provider.payoutAccount !== "enabled" ? "Stripe hesabı hazır değil" : "")}
        </span>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Hizmet veren ödemeleri"
        description="Takılanlar her turda otomatik yeniden denenir; iade borcu sonraki ödemeden kesilir (K-047)."
        actions={
          <Tabs value={view} onValueChange={(v) => setView(v as keyof typeof VIEWS)}>
            <TabsList>
              {Object.entries(VIEWS).map(([k, v]) => (
                <TabsTrigger key={k} value={k}>
                  {v}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.payouts}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Bu görünümde ödeme yok."
      />
    </>
  );
}
